/**
 * レッスンのインデックス（ユニット別の並び、カード → レッスン）。ブラウザで使う（remark を含まない）。
 */
import type { LessonDoc, LessonMeta } from './types.js';

export interface LessonUnitEntry {
  unit: string;
  /** order 昇順。draft は含めない。 */
  lessonIds: string[];
}

export interface LessonIndex {
  /** 一覧に出すレッスン（draft を除く）。ユニット順 → order 昇順。 */
  lessons: LessonDoc[];
  /** draft も含む（id で開けるようにする）。 */
  byId: ReadonlyMap<string, LessonDoc>;
  units: LessonUnitEntry[];
  /** カード id → そのカードを cards / memos / {write card} に載せるレッスン（draft を除く）。 */
  lessonsOfCard: ReadonlyMap<string, string[]>;
}

export interface BuildLessonIndexOptions {
  /** ユニットの並び（カードのカタログの units の順など）。載っていないユニットは後ろに名前順。 */
  unitOrder?: readonly string[];
}

function cmp(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** 本文の {write card="…"} が使うカード id（出現順）。 */
export function writeCardIds(doc: LessonDoc): string[] {
  return doc.writes.flatMap(({ block }) => ('card' in block ? [block.card] : []));
}

/**
 * レッスンのドリルの出題順（lesson-format.md §8.1）: cards（並べた順）→ memos（並べた順）。
 * isPublished が false のカード（draft・content に無い）は除く。
 */
export function lessonDrillCardIds(
  meta: Pick<LessonMeta, 'cards' | 'memos'>,
  isPublished: (cardId: string) => boolean,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of [...meta.cards, ...meta.memos]) {
    if (seen.has(id) || !isPublished(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** 完了の判定に使う、レッスンの実験と書く課題の id（§8.2）。 */
export function lessonRequiredSteps(doc: LessonDoc): { experiments: string[]; writes: string[] } {
  return {
    experiments: doc.experiments.map((e) => e.block.id),
    writes: doc.writes.map((w) => w.block.id),
  };
}

export function buildLessonIndex(
  docs: readonly LessonDoc[],
  opts: BuildLessonIndexOptions = {},
): LessonIndex {
  const rank = new Map((opts.unitOrder ?? []).map((u, i) => [u, i]));
  const unitRank = (u: string): number => rank.get(u) ?? Number.MAX_SAFE_INTEGER;
  const sorted = [...docs].sort(
    (a, b) =>
      unitRank(a.meta.unit) - unitRank(b.meta.unit) ||
      cmp(a.meta.unit, b.meta.unit) ||
      a.meta.order - b.meta.order ||
      cmp(a.meta.id, b.meta.id),
  );
  const byId = new Map<string, LessonDoc>();
  for (const d of sorted) if (!byId.has(d.meta.id)) byId.set(d.meta.id, d);
  const lessons = [...byId.values()].filter((d) => d.meta.draft !== true);
  const units: LessonUnitEntry[] = [];
  const lessonsOfCard = new Map<string, string[]>();
  for (const d of lessons) {
    let u = units[units.length - 1];
    if (u?.unit !== d.meta.unit) {
      u = { unit: d.meta.unit, lessonIds: [] };
      units.push(u);
    }
    u.lessonIds.push(d.meta.id);
    for (const cardId of new Set([...d.meta.cards, ...d.meta.memos, ...writeCardIds(d)])) {
      const list = lessonsOfCard.get(cardId) ?? [];
      list.push(d.meta.id);
      lessonsOfCard.set(cardId, list);
    }
  }
  return { lessons, byId, units, lessonsOfCard };
}
